<script setup>
import { ref, computed, watch, onMounted } from "vue";
import {
  getPigmentos,
  crearPigmento,
  actualizarPigmento,
  eliminarPigmento,
  subirFichaTecnicaPigmento,
  eliminarFichaTecnicaPigmento,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { usePaginacionPigmentosStore } from "@/store/paginacionStore";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionPigmentosStore();

const registros = ref([]);
const filtro = ref("");
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const paginaActual = ref(1);
const modalElement = ref(null);
let modalInstance = null;

const form = ref({ id: null, nombre: "", codigo: "", hexadecimal: "#000000", rgb: "" });

// ── Sincronizar RGB desde el color picker ──────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

function actualizarDesdeHex() {
  const hex = form.value.hexadecimal;
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return;
  const { r, g, b } = hexToRgb(hex);
  form.value.rgb = `${r},${g},${b}`;
}

// ── Ordenamiento ───────────────────────────────────────────
const sortKey = ref("nombre");
const sortDir = ref("asc");

function onSort({ field, asc }) {
  sortKey.value = field;
  sortDir.value = asc ? "asc" : "desc";
  paginaActual.value = 1;
}

watch(filtro, () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const filtrados = computed(() => {
  const s = filtro.value.toLowerCase();
  return registros.value.filter(
    (r) =>
      r.nombre.toLowerCase().includes(s) ||
      (r.codigo || "").toLowerCase().includes(s) ||
      r.hexadecimal.toLowerCase().includes(s)
  );
});

const ordenados = computed(() =>
  [...filtrados.value].sort((a, b) => {
    const cmp = String(a[sortKey.value] ?? "").localeCompare(
      String(b[sortKey.value] ?? ""),
      "es",
      { sensitivity: "base" }
    );
    return sortDir.value === "asc" ? cmp : -cmp;
  })
);

const porPagina = computed(() => paginacionStore.porPagina);
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(ordenados.value.length / porPagina.value))
);
const paginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return ordenados.value.slice(inicio, inicio + porPagina.value);
});
const rangoInicio = computed(() =>
  ordenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, ordenados.value.length)
);
const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const actual = paginaActual.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, actual]);
  if (actual > 1) set.add(actual - 1);
  if (actual < total) set.add(actual + 1);
  const ordenadas = [...set].sort((a, b) => a - b);
  const resultado = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push("...");
    resultado.push(ordenadas[i]);
  }
  return resultado;
});

function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── CRUD ───────────────────────────────────────────────────
async function cargar() {
  loading.value = true;
  try {
    registros.value = await getPigmentos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los pigmentos", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(item = null) {
  if (item) {
    editando.value = true;
    form.value = {
      id: item.id,
      nombre: item.nombre,
      codigo: item.codigo || "",
      hexadecimal: item.hexadecimal,
      rgb: item.rgb,
    };
  } else {
    editando.value = false;
    form.value = {
      id: null,
      nombre: "",
      codigo: "",
      hexadecimal: "#000000",
      rgb: "0,0,0",
    };
  }
  modalInstance.show();
}

async function guardar() {
  guardando.value = true;
  try {
    const payload = {
      nombre: form.value.nombre,
      codigo: form.value.codigo,
      hexadecimal: form.value.hexadecimal,
      rgb: form.value.rgb,
    };
    if (editando.value) {
      await actualizarPigmento(form.value.id, payload);
    } else {
      await crearPigmento(payload);
    }
    await cargar();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Pigmento ${editando.value ? "actualizado" : "registrado"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar el pigmento", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(item) {
  const res = await Swal.fire({
    title: `¿Desactivar "${item.nombre}"?`,
    text: "El pigmento será marcado como inactivo.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, desactivar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarPigmento(item.id);
      await cargar();
      Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Pigmento desactivado",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire("Error", "No se pudo desactivar el pigmento", "error");
    }
  }
}

// ── Documentos Pigmento (Ficha técnica) ─────────────────────
const modalDocElement = ref(null);
let modalDocInstance = null;
const colorDoc = ref(null); // Pigmento activo en modal de documentos
const subiendoFicha = ref(false);
const fileRefFicha = ref(null);

// Retorna el documento más reciente de un array (último subido)
function ultimoDoc(arr) {
  if (!arr || arr.length === 0) return null;
  return arr[arr.length - 1];
}

function abrirModalDoc(item) {
  colorDoc.value = { ...item };
  modalDocInstance.show();
}

async function handleSubirFicha(e) {
  const file = e.target.files[0];
  if (!file) return;
  subiendoFicha.value = true;
  try {
    const fd = new FormData();
    fd.append("documento", file);
    const nuevo = await subirFichaTecnicaPigmento(colorDoc.value.id, fd);
    // Actualizar local sin refetch
    const idx = registros.value.findIndex((r) => r.id === colorDoc.value.id);
    if (idx !== -1) {
      registros.value[idx].fichas_tecnicas = [
        ...(registros.value[idx].fichas_tecnicas || []),
        nuevo,
      ];
      colorDoc.value = { ...registros.value[idx] };
    }
    Swal.fire({
      icon: "success",
      title: "Ficha técnica subida",
      timer: 1800,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo subir la ficha técnica", "error");
  } finally {
    subiendoFicha.value = false;
    if (fileRefFicha.value) fileRefFicha.value.value = "";
  }
}

async function confirmarEliminarDoc(doc) {
  const res = await Swal.fire({
    title: `¿Eliminar este documento?`,
    text: "Esta acción no se puede deshacer.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (!res.isConfirmed) return;
  try {
    await eliminarFichaTecnicaPigmento(doc.id);
    const idx = registros.value.findIndex((r) => r.id === colorDoc.value.id);
    if (idx !== -1) {
      registros.value[idx].fichas_tecnicas = registros.value[idx].fichas_tecnicas.filter(
        (f) => f.id !== doc.id
      );
      colorDoc.value = { ...registros.value[idx] };
    }
    Swal.fire({
      icon: "success",
      title: "Documento eliminado",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo eliminar el documento", "error");
  }
}

onMounted(() => {
  cargar();
  modalInstance = new Modal(modalElement.value);
  modalDocInstance = new Modal(modalDocElement.value);
});
</script>

<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Pigmentos"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre, código o hex..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-header" @click="abrirModal(null)">
          <i class="bi bi-palette-fill"></i>
          <span class="btn-header-texto ms-2">Nuevo pigmento</span>
        </button>
      </template>
    </CrudToolbar>

    <!-- TABLA ESCRITORIO -->
    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4" style="width: 64px">Color</th>
            <th>
              <ColumnFilterMenu
                field="nombre"
                label="Nombre"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="codigo"
                label="Código"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="hexadecimal"
                label="HEX"
                :filterable="false"
                :items="filtrados"
                :sortField="sortKey"
                :sortAsc="sortDir === 'asc'"
                @update:sort="onSort"
              />
            </th>
            <th>RGB</th>
            <th class="text-center col-doc">Ficha técnica</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginados" :key="item.id">
            <td class="ps-4">
              <span
                class="color-swatch"
                :style="{ backgroundColor: item.hexadecimal }"
              ></span>
            </td>
            <td class="fw-bold text-cafe">{{ item.nombre }}</td>
            <td>
              <code v-if="item.codigo" class="hex-code">{{ item.codigo }}</code>
              <span v-else class="text-cafe opacity-40 small">—</span>
            </td>
            <td>
              <code class="hex-code">{{ item.hexadecimal }}</code>
            </td>
            <td class="fw-bold">({{ item.rgb }})</td>

            <!-- Ficha técnica -->
            <td class="text-center">
              <a
                v-if="ultimoDoc(item.fichas_tecnicas)"
                :href="ultimoDoc(item.fichas_tecnicas).documento"
                target="_blank"
                class="btn-doc"
                title="Ver ficha técnica"
              >
                <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ver PDF
              </a>
              <span v-else class="text-cafe opacity-40 small">—</span>
            </td>

            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button
                  class="btn-circular"
                  @click="abrirModalDoc(item)"
                  title="Documentos"
                >
                  <i class="bi bi-folder2-open"></i>
                </button>
                <button class="btn-circular" @click="abrirModal(item)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(item)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="paginados.length === 0 && !loading">
            <td colspan="7" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron pigmentos
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación escritorio -->
      <div class="paginacion-wrapper">
        <div class="pag-izq">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} pigmentos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-der">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="paginaActual = 1"
            title="Primera"
          >
            <i class="bi bi-chevron-double-left"></i>
          </button>
          <button class="btn-pag" :disabled="paginaActual === 1" @click="paginaActual--">
            <i class="bi bi-chevron-left"></i>
          </button>
          <template v-for="p in paginasVisibles" :key="p">
            <span v-if="p === '...'" class="pag-ellipsis">…</span>
            <button
              v-else
              :class="['btn-pag-num', { activo: p === paginaActual }]"
              @click="paginaActual = p"
            >
              {{ p }}
            </button>
          </template>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual = totalPaginas"
            title="Última"
          >
            <i class="bi bi-chevron-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- CARDS MÓVIL -->
    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="item in paginados"
        :key="'mob-' + item.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex align-items-center gap-3 mb-2">
          <span
            class="color-swatch-lg"
            :style="{ backgroundColor: item.hexadecimal }"
          ></span>
          <div>
            <div class="fw-bold text-cafe card-name">{{ item.nombre }}</div>
            <code class="hex-code">{{ item.hexadecimal }}</code>
            <code v-if="item.codigo" class="hex-code ms-1">{{ item.codigo }}</code>
          </div>
        </div>
        <!-- Ficha técnica en móvil -->
        <div class="d-flex gap-2 mb-2 flex-wrap">
          <a
            v-if="ultimoDoc(item.fichas_tecnicas)"
            :href="ultimoDoc(item.fichas_tecnicas).documento"
            target="_blank"
            class="btn-doc"
          >
            <i class="bi bi-file-earmark-pdf-fill me-1"></i>Ficha técnica
          </a>
        </div>
        <div class="d-flex justify-content-end gap-3 pt-2 border-top">
          <i
            class="bi bi-folder2-open text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModalDoc(item)"
            title="Gestionar documentos"
          ></i>
          <i
            class="bi bi-pencil-square text-verde fs-5"
            style="cursor: pointer"
            @click="abrirModal(item)"
          ></i>
          <i
            class="bi bi-trash3-fill text-danger fs-5"
            style="cursor: pointer"
            @click="confirmarBorrado(item)"
          ></i>
        </div>
      </div>
      <div class="paginacion-mobile">
        <div class="pag-mobile-top">
          <small class="pag-info"
            >{{ rangoInicio }}–{{ rangoFin }} de {{ ordenados.length }} pigmentos</small
          >
          <div class="por-pagina-wrap">
            <label class="por-pagina-label">Filas:</label>
            <select
              class="por-pagina-select"
              :value="porPagina"
              @change="cambiarPorPagina(Number($event.target.value))"
            >
              <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>
        </div>
        <div class="pag-mobile-nav">
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === 1"
            @click="paginaActual--"
          >
            <i class="bi bi-chevron-left"></i> Anterior
          </button>
          <small class="text-verde fw-bold"
            >{{ paginaActual }} / {{ totalPaginas }}</small
          >
          <button
            class="btn-pag-mobile"
            :disabled="paginaActual === totalPaginas"
            @click="paginaActual++"
          >
            Siguiente <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalElement">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar pigmento" : "Nuevo pigmento" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardar">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre <span class="text-danger">*</span></label
                >
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Rojo cereza"
                />
              </div>

              <div class="mb-3">
                <label class="form-label label-divisan">Código (opcional)</label>
                <input
                  v-model="form.codigo"
                  type="text"
                  class="form-control divisan-input-modal"
                  placeholder="Ej. ML-1001"
                />
              </div>

              <!-- Color picker + hex -->
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Color <span class="text-danger">*</span></label
                >
                <div class="color-picker-wrap">
                  <input
                    v-model="form.hexadecimal"
                    type="color"
                    class="color-picker-input"
                    @input="actualizarDesdeHex"
                  />
                  <input
                    v-model="form.hexadecimal"
                    type="text"
                    class="form-control divisan-input-modal hex-text-input"
                    placeholder="#FF5733"
                    maxlength="7"
                    @input="actualizarDesdeHex"
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label label-divisan">RGB</label>
                <input
                  v-model="form.rgb"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="255,87,51"
                />
              </div>
              <small class="text-muted d-block mt-2">
                <i class="bi bi-info-circle me-1 text-verde"></i>
                RGB se calcula automáticamente.
              </small>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar pigmento" : "Guardar pigmento" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- MODAL DOCUMENTOS -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalDocElement">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <div>
              <h5 class="modal-title fw-bold text-cafe mb-0">Documentos del pigmento</h5>
              <small class="text-cafe opacity-60 fw-bold">{{ colorDoc?.nombre }}</small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div class="row g-4" v-if="colorDoc">
              <!-- ── Ficha técnica ── -->
              <div class="col-12">
                <div class="doc-seccion">
                  <div class="doc-seccion-header">
                    <i class="bi bi-file-earmark-pdf-fill text-danger me-2"></i>
                    <span class="fw-bold text-cafe">Fichas técnicas</span>
                    <span class="badge-conteo ms-2">{{
                      (colorDoc.fichas_tecnicas || []).length
                    }}</span>
                  </div>

                  <!-- Lista de fichas existentes -->
                  <div class="doc-lista mt-2">
                    <div
                      v-for="doc in colorDoc.fichas_tecnicas || []"
                      :key="'ft-' + doc.id"
                      class="doc-item"
                    >
                      <a :href="doc.documento" target="_blank" class="doc-link">
                        <i class="bi bi-file-earmark-pdf me-1"></i>
                        {{
                          doc.fecha_subida
                            ? new Date(doc.fecha_subida).toLocaleDateString("es-MX")
                            : "Sin fecha"
                        }}
                      </a>
                      <button
                        class="btn-doc-eliminar"
                        @click="confirmarEliminarDoc(doc)"
                        title="Eliminar"
                      >
                        <i class="bi bi-trash3-fill"></i>
                      </button>
                    </div>
                    <p
                      v-if="!(colorDoc.fichas_tecnicas || []).length"
                      class="text-cafe opacity-40 small mb-0"
                    >
                      Sin fichas registradas
                    </p>
                  </div>

                  <!-- Subir nueva -->
                  <div class="mt-3">
                    <label class="form-label label-divisan small"
                      >Subir nueva ficha</label
                    >
                    <input
                      ref="fileRefFicha"
                      type="file"
                      accept="application/pdf"
                      class="form-control divisan-input-modal"
                      :disabled="subiendoFicha"
                      @change="handleSubirFicha"
                    />
                    <div
                      v-if="subiendoFicha"
                      class="d-flex align-items-center gap-2 mt-2"
                    >
                      <div class="spinner-border spinner-border-sm text-verde"></div>
                      <small class="text-cafe">Subiendo...</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-nuevo px-4" data-bs-dismiss="modal">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-cafe {
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.88rem;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: #703808;
  color: var(--d-cream);
}
.btn-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper {
  position: relative;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
  pointer-events: none;
}
.search-wrapper input {
  padding-left: 35px;
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}

.color-swatch {
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid rgba(64, 34, 8, 0.15);
  vertical-align: middle;
}
.color-swatch-lg {
  display: inline-block;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  border: 2px solid rgba(64, 34, 8, 0.15);
  flex-shrink: 0;
}
.hex-code {
  background: rgba(96, 108, 56, 0.1);
  color: var(--d-green-dark);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.82rem;
}

.color-picker-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.color-picker-input {
  width: 48px;
  height: 42px;
  border: 2px solid var(--d-green);
  border-radius: 8px;
  cursor: pointer;
  padding: 2px;
  background: white;
  flex-shrink: 0;
}
.hex-text-input {
  flex: 1;
  font-family: var(--d-font-mono);
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}

/* El modal centrado quedaba pegado (a veces debajo) del navbar fijo; se
   empuja un poco hacia abajo en todos los tamaños. */
.modal-dialog {
  margin-top: 4.5rem;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}

.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  min-width: 58px;
}
.por-pagina-select:focus,
.por-pagina-select:hover {
  border-color: var(--d-brown);
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}

.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}

.card-name {
  font-size: 0.92rem;
}

@media (max-width: 991.98px) {
  .btn-header-texto {
    display: none;
  }
  .btn-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .paginacion-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* ── Columna de documento ── */
.col-doc {
  width: 130px;
}

/* Botón Ver PDF en tabla */
.btn-doc {
  display: inline-flex;
  align-items: center;
  background: rgba(220, 53, 69, 0.08);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn-doc:hover {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

/* Modal de documentos */
.doc-seccion {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.2);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
}
.doc-seccion-header {
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(64, 34, 8, 0.08);
}
.badge-conteo {
  background: rgba(96, 108, 56, 0.15);
  color: var(--d-green-dark);
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 800;
}
.doc-lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 40px;
}
.doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(96, 108, 56, 0.05);
  border-radius: 6px;
  padding: 5px 10px;
}
.doc-link {
  color: var(--d-brown);
  font-size: 0.82rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  flex: 1;
}
.doc-link:hover {
  color: var(--d-green);
  text-decoration: underline;
}
.btn-doc-eliminar {
  background: none;
  border: none;
  color: rgba(220, 53, 69, 0.5);
  padding: 2px 6px;
  cursor: pointer;
  transition: color 0.15s;
  font-size: 0.8rem;
  flex-shrink: 0;
}
.btn-doc-eliminar:hover {
  color: #dc3545;
}
</style>
